/**
 * The `$var.<SCOPE>.<name>` grammar for an external runner's `envMapping`.
 *
 * @module runner-env-mapping
 * @description A runner's `envMapping` is `{ ".env key" -> source }` where a
 * source is either `$var.USER.<name>`, `$var.ENV.<name>`, or a literal. The
 * mapping is BOTH the materialization map and the secret-injection allowlist
 * (see `apps/api/src/external-tests/env-materializer.ts`), so the grammar that
 * decides "is this a variable reference or a literal string?" is
 * security-critical.
 *
 * It lives here, in `@chatsdet/types`, because BOTH sides need it and neither
 * can import the other: the API materializes the `.env`, and the web config
 * editor has to render, parse and write the same tokens. A second client-side
 * copy is exactly the drift that ends with the literal string `"$var.ENV.FOO"`
 * being written into a container's `.env` — a mapping the editor believes is a
 * variable reference and the materializer believes is a literal, which fails at
 * login twenty minutes into a run with nothing pointing at the cause.
 *
 * Pure string functions only, with no zod schema and no I/O: the materializer
 * imports it inside an Effect pipeline and the SPA imports it into a render
 * path, so it must stay free of both.
 */

/** Scopes a mapping source may reference. `USER` = persona, `ENV` = environment. */
export const RUNNER_ENV_VAR_SCOPES = ['USER', 'ENV'] as const;
export type RunnerEnvVarScope = (typeof RUNNER_ENV_VAR_SCOPES)[number];

/**
 * A parsed `envMapping` source.
 *
 * Discriminated rather than "scope is null for a literal", because a literal
 * carries a value and a reference carries a name, and collapsing them into one
 * optional-field shape is how a caller ends up resolving a literal as if it
 * were a variable name.
 */
export type RunnerEnvSourceRef =
  | { kind: 'var'; scope: RunnerEnvVarScope; name: string }
  | { kind: 'literal'; value: string };

/**
 * Whole-value match only — a source is a reference or it is not.
 *
 * Deliberately NOT an interpolating grammar: `https://$var.ENV.host/app` stays a
 * literal. Substring interpolation would mean the allowlist had to reason about
 * partial secrets inside a larger string, and every value written to the `.env`
 * would need scrubbing at a character level rather than a value level.
 */
const VAR_TOKEN = /^\$var\.(USER|ENV)\.(.+)$/;

/** Parse one `envMapping` source. Never throws; anything unmatched is a literal. */
export function parseRunnerEnvSource(source: string): RunnerEnvSourceRef {
  const match = VAR_TOKEN.exec(source);
  if (!match) return { kind: 'literal', value: source };
  return { kind: 'var', scope: match[1] as RunnerEnvVarScope, name: match[2] };
}

/** Build the canonical token for a scope + name. The only way to write one. */
export function formatRunnerEnvVar(scope: RunnerEnvVarScope, name: string): string {
  return `$var.${scope}.${name}`;
}

/**
 * Which scopes a whole mapping references.
 *
 * Drives lazy decryption on the API side (no credential read when the mapping is
 * all literals) and the "which pickers do we need data for" decision on the web
 * side — one function so those two answers cannot disagree.
 */
export function referencedRunnerEnvScopes(
  mapping: Record<string, string>
): Record<RunnerEnvVarScope, boolean> {
  const seen: Record<RunnerEnvVarScope, boolean> = { USER: false, ENV: false };
  for (const source of Object.values(mapping)) {
    const parsed = parseRunnerEnvSource(source);
    if (parsed.kind === 'var') seen[parsed.scope] = true;
  }
  return seen;
}
